// TASK 옵션 이름과 평가 대상 policy 의 학습 dataset 이름을 비교해 가장 적합한 옵션 하나를 고른다.
// 옵션 이름 규약은 `C6-{task}-{hand}-{version}`(예: C6-SugarBox-SigmaHand-v0)이다. 그러나 이미지마다
// 규약 밖 이름(`pick`)도 온다. 그래서 세그먼트 구조만 쓰고 규약을 강제하지 않는다.
//
// 두 이름은 구분자 관례가 서로 다르다(`SugarBox` vs `sugar_box` vs `sugarbox`). 그래서 토큰 일치가
// 아니라, 구분자를 지운 compact 문자열이 부분 문자열로 포함되는지 비교한다.
//
// 잘못된 추천은 사용자가 알아채기 어렵다. 그래서 판정을 보수적으로 한다.
//   - task 이름(primary)이 절반 이상 맞지 않으면 후보에서 뺀다.
//   - 총점이 임계 미만이면 추천하지 않는다. 그때 호출부는 이미지가 준 기본 TASK 를 그대로 쓴다.

const VENDOR_PREFIX = "c6";
const VERSION_SEGMENT_RE = /^v\d+$/i;

// task 이름은 hand 이름 같은 나머지 수식어보다 가중치가 크다.
const PRIMARY_WEIGHT = 0.6;
// primary 게이트. hand 이름만 겹치는 옵션이 총점으로 올라오지 못하게 막는다.
const MIN_PRIMARY_SCORE = 0.5;
const MIN_SCORE = 0.5;

// 이름 한 조각(`SugarBox`)과 그 하위 토큰(`sugar`, `box`).
type NameSegment = {
  tokens: readonly string[];
  length: number;
};

type TaskOption = {
  name: string;
  primary: NameSegment;
  // primary 를 뺀 나머지 식별 세그먼트(hand 이름, `Mimic` 같은 변형 표기).
  secondaries: readonly NameSegment[];
};

type OptionTally = {
  option: TaskOption;
  score: number;
  // 동점 tie-break. 실제로 맞은 식별 글자 수가 많은(= 더 구체적인) 옵션을 앞에 둔다.
  matchedChars: number;
};

// camelCase 와 문자/숫자 경계를 토큰 경계로 본다. 영숫자가 아닌 문자는 모두 구분자로 보고 버린다.
function tokenize(value: string): string[] {
  return value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Za-z])(\d)/g, "$1 $2")
    .replace(/(\d)([A-Za-z])/g, "$1 $2")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((token) => token !== "");
}

function toSegment(value: string): NameSegment | null {
  const tokens = tokenize(value);
  if (tokens.length === 0) {
    return null;
  }
  return { tokens, length: tokens.reduce((sum, token) => sum + token.length, 0) };
}

// 벤더 접두사와 버전 세그먼트를 뺀 나머지가 식별 세그먼트다. 남는 것이 없으면 판정할 근거가 없다.
function parseTaskOption(name: string): TaskOption | null {
  let segments = name.split("-").filter((segment) => segment.trim() !== "");
  if (segments.length > 0 && VERSION_SEGMENT_RE.test(segments[segments.length - 1])) {
    segments = segments.slice(0, -1);
  }
  if (segments.length > 0 && segments[0].toLowerCase() === VENDOR_PREFIX) {
    segments = segments.slice(1);
  }

  const parsed = segments
    .map(toSegment)
    .filter((segment): segment is NameSegment => segment !== null);
  if (parsed.length === 0) {
    return null;
  }
  return { name, primary: parsed[0], secondaries: parsed.slice(1) };
}

// 세그먼트 글자 중 dataset 이름에 들어 있는 글자 수. 세그먼트 길이로 나누면 부분 일치 비율이다.
function segmentMatchedChars(segment: NameSegment, datasetCompact: string): number {
  let matched = 0;
  for (const token of segment.tokens) {
    if (datasetCompact.includes(token)) {
      matched += token.length;
    }
  }
  return matched;
}

// dataset 이름 하나에 대한 옵션 점수. 임계 미만은 0 으로 버려 합산에서 뺀다.
const NO_MATCH = { score: 0, matchedChars: 0 } as const;

function scoreOption(
  option: TaskOption,
  datasetCompact: string,
): { score: number; matchedChars: number } {
  const primaryMatched = segmentMatchedChars(option.primary, datasetCompact);
  const primary = primaryMatched / option.primary.length;
  if (primary < MIN_PRIMARY_SCORE) {
    return NO_MATCH;
  }

  const secondaryMatched = option.secondaries.map((segment) =>
    segmentMatchedChars(segment, datasetCompact),
  );
  let score = primary;
  if (option.secondaries.length > 0) {
    const secondary =
      secondaryMatched.reduce(
        (sum, matched, index) => sum + matched / option.secondaries[index].length,
        0,
      ) / option.secondaries.length;
    score = primary * PRIMARY_WEIGHT + secondary * (1 - PRIMARY_WEIGHT);
  }
  if (score < MIN_SCORE) {
    return NO_MATCH;
  }

  return {
    score,
    matchedChars: secondaryMatched.reduce((sum, matched) => sum + matched, primaryMatched),
  };
}

// sort 는 안정 정렬이다. 그래서 점수와 설명량이 같으면 옵션 배열 순서가 그대로 남는다.
function compareTally(a: OptionTally, b: OptionTally): number {
  return b.score - a.score || b.matchedChars - a.matchedChars;
}

// 선택한 policy 들의 모든 dataset 이름과 비교해 총점 1위 옵션을 반환한다. 비슷한 옵션이 없으면 null.
export function suggestTaskOption(
  options: readonly string[],
  datasetNames: readonly string[],
): string | null {
  const datasetCompacts = datasetNames
    .map((name) => tokenize(name).join(""))
    .filter((compact) => compact !== "");
  if (datasetCompacts.length === 0) {
    return null;
  }

  const tallies: OptionTally[] = [];
  for (const name of options) {
    const option = parseTaskOption(name);
    if (option === null) {
      continue;
    }
    const tally: OptionTally = { option, score: 0, matchedChars: 0 };
    for (const compact of datasetCompacts) {
      const scored = scoreOption(option, compact);
      tally.score += scored.score;
      tally.matchedChars += scored.matchedChars;
    }
    tallies.push(tally);
  }

  const best = tallies.sort(compareTally)[0];
  return best === undefined || best.score === 0 ? null : best.option.name;
}
